import { FeatureName, RoutineWorkflow, useIsFeatureEnabled, useRoutineAlerts } from '@gripp/shared-logic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScrollView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { Colors } from '../../../themes/colors';
import { Button } from '../../crop-circle/Button/Button';
import { Icon } from '../../crop-circle/icons';
import { tokens } from '../../crop-circle/tokens';
import { BottomComponentBar } from '../../navigation/bottomComponentBar';
import LoadingIndicator from '../../loadingIndicator';
import { AlertSummary } from './alertSummary';
import { AlertListOld } from './alertList-old';
import { EditAlertModal } from './editAlertModal';
import { NewAlertModal } from './newAlertModal';

type AlertListProps = {
  workspaceAssetId: string;
  routineId: string;
  newAlertModalVisible: boolean;
  editAlertModalVisible: boolean;
  editAlertModalStyle: StyleProp<ViewStyle>;
  onClose: () => void;
  handleShowNewAlertModal: () => void;
  handleCloseNewAlertModal: () => void;
  handleShowEditAlertModal: () => void;
  handleCloseEditAlertModal: () => void;
  productId?: string;
};

export const AlertList = ({
  workspaceAssetId,
  routineId,
  newAlertModalVisible,
  editAlertModalVisible,
  editAlertModalStyle,
  onClose,
  handleShowNewAlertModal,
  handleCloseNewAlertModal,
  handleShowEditAlertModal,
  handleCloseEditAlertModal,
  productId,
}: AlertListProps) => {
  // TODO: Crop Circle Redesign: Remove after feature is fully rolled out
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  // TODO: Crop Circle Redesign: Remove old AlertList after feature is fully rolled out
  if (!isCropCircleRedesignEnabled) {
    return (
      <AlertListOld
        {...{
          workspaceAssetId,
          routineId,
          newAlertModalVisible,
          editAlertModalVisible,
          editAlertModalStyle,
          onClose,
          handleShowNewAlertModal,
          handleCloseNewAlertModal,
          handleShowEditAlertModal,
          handleCloseEditAlertModal,
          productId,
        }}
      />
    );
  }

  // TODO: Crop Circle Redesign: Remove to AlertListBase after feature is fully rolled out
  return (
    <AlertListBase
      {...{
        workspaceAssetId,
        routineId,
        newAlertModalVisible,
        editAlertModalVisible,
        editAlertModalStyle,
        onClose,
        handleShowNewAlertModal,
        handleCloseNewAlertModal,
        handleShowEditAlertModal,
        handleCloseEditAlertModal,
        productId,
      }}
    />
  );
};

export const AlertListBase = ({
  workspaceAssetId,
  routineId,
  newAlertModalVisible,
  editAlertModalVisible,
  editAlertModalStyle,
  onClose,
  handleShowNewAlertModal,
  handleCloseNewAlertModal,
  handleShowEditAlertModal,
  handleCloseEditAlertModal,
  productId,
}: AlertListProps) => {
  const { t } = useTranslation();
  const [alertSummaryId, setAlertSummaryId] = useState<string>('');

  const {
    routineWorkflows,
    workspaceAsset,
    isLoading: isRoutineAlertsLoading,
  } = useRoutineAlerts(workspaceAssetId, routineId, productId);

  const onAlertSelected = (alert: RoutineWorkflow) => {
    const alertId = alert?.id;

    if (!alertId) return;

    setAlertSummaryId(alertId);
    handleShowEditAlertModal();
  };

  const RenderAlerts = () => {
    return routineWorkflows?.map((item: any, index: number) => {
      return (
        <AlertSummary
          onAlertSelected={onAlertSelected}
          key={`${index}-${item.id}`}
          workflow={item}
          workspaceAsset={workspaceAsset}
          routineId={routineId}
        />
      );
    });
  };

  const isLoading = isRoutineAlertsLoading;

  return (
    <>
      <View style={styles.container}>
        {isLoading ? (
          <View
            style={{
              flex: 1,
            }}
          >
            <LoadingIndicator
              backgroundColor={Colors.primaryGrayBackground}
              flexContainer
            />
          </View>
        ) : (
          <View style={styles.scrollViewContainer}>
            <ScrollView>
              <RenderAlerts />
              <Button
                variant="rectangular"
                size="large"
                state="default"
                onPress={handleShowNewAlertModal}
                leftIcon={<Icon name="Add" size={20} color={tokens.colors.brand.black} />}
                style={styles.createAlertButton}
              >
                {t('alerts.setAlert')}
              </Button>
            </ScrollView>
          </View>
        )}
        <BottomComponentBar
          backButton={
            <Button
              variant="rounded"
              state="active"
              onPress={onClose}
              style={styles.backButton}
              leftIcon={<Icon name="Chevron Left" size={24} color={tokens.colors.neutral[100]} />}
            />
          }
        />
      </View>
      {
        <NewAlertModal
          newAlertModalVisible={newAlertModalVisible}
          onClose={handleCloseNewAlertModal}
          workspaceAssetId={workspaceAssetId}
          routineId={routineId}
          productId={productId}
        />
      }

      {alertSummaryId && (
        <EditAlertModal
          editAlertModalVisible={editAlertModalVisible}
          onClose={handleCloseEditAlertModal}
          workspaceAssetId={workspaceAssetId}
          routineId={routineId}
          alertId={alertSummaryId}
          editAlertModalStyle={editAlertModalStyle}
          productId={productId}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.primaryGrayBackground,
  },
  scrollViewContainer: {
    flex: 1,
    margin: tokens.spacing.xl,
    paddingBottom: 80, // Space for bottom bar
  },
  createAlertButton: {
    marginTop: tokens.spacing.md,
  },
  backButton: {
    width: 56,
    height: 56,
    minWidth: 56,
    maxHeight: 56,
    borderRadius: 99,
    marginLeft: tokens.spacing.lg,
  },
});
